/**
 * loadoutSelection.ts -- TheBurntPeanut Simulator 9000
 *
 * Tracks which PEANUT PERCS, BUNCHA BULLSHIIII items, and WEAPONS the player
 * has checked (toggled ON) in the loadout grids before deploying.
 *
 * ── HOW THE MAIN GAME BUILD SHOULD USE THIS ──
 *
 * 1. Import { getActivePercs, getActiveBullshiiiiItems, getActiveWeapons, isWeaponUnlocked } from './loadoutSelection';
 *
 * 2. At the start of a run (after DEPLOY countdown finishes):
 *      const percs    = getActivePercs();           // PeanutPerc[] — checked percs
 *      const items    = getActiveBullshiiiiItems();  // BullshiiiiItem[] — checked items in the drop pool
 *      const weapons  = getActiveWeapons();          // string[] — checked weapon IDs
 *
 * 3. Use `percs` to apply permanent stat bonuses for the run.
 *    Use `items` as the pool of items that can drop from enemies/chests.
 *    Use `weapons` to know which weapons are in the player's active pool.
 *
 * 4. Use `isWeaponUnlocked(weaponId)` to check if a weapon is unlocked.
 *    Characters whose weapon is locked should also be locked on the select screen.
 *
 * 5. Selections persist in localStorage so the player's choices survive
 *    page refreshes and return visits.
 *
 * ── RULES ──
 * - Only UNLOCKED items can be checked.
 * - All unlocked items start CHECKED by default (opt-out model).
 * - Locked items are always unchecked and cannot be toggled.
 * - WEAPONS: Only 'peanut_rifle' and 'revolver' are unlocked by default.
 * - Characters are locked if their weapon is locked.
 */

import { BUNCHA_BULLSHIIII, type BullshiiiiItem } from './bunchaBullshiiii';
import { PEANUT_PERCS, type PeanutPerc } from './peanutPercs';
import { resolveAsset } from './uiAssets';

// ── Weapon definitions ───────────────────────────────────────────────────────
export interface WeaponDef {
  id: string;
  name: string;
  characterId: string;
  characterName: string;
  iconUrl: string;
  unlocked: boolean;
  unlockReq: string;
  /** BETA 19.35 — Dylan: "there should be a quest for every weapon to
   *  unlock for new users". Shared weapons (characterId='shared') used
   *  to be `unlocked: true` from the start with no progression. Now they
   *  each have an optional unlockCondition checked against lifetimeStats. */
  unlockCondition?: {
    statKey: 'totalKills' | 'totalDeaths' | 'bossesKilled' | 'totalRunsPlayed'
            | 'totalGoldCollected' | 'totalChestsOpened' | 'totalDamageDealt'
            | 'totalDamageTaken' | 'totalHealingDone' | 'totalCriticalHits'
            | 'longestSurvivalMinutes' | 'highestLevelReached';
    value: number;
    description: string;
  };
}

/** BETA 19.35 — Light snapshot of LifetimeStats fields used for weapon
 *  unlock checks. Defined here (not imported from goopSystem) to avoid a
 *  circular dependency. Caller passes the subset. */
export interface WeaponUnlockStats {
  totalKills?: number; totalDeaths?: number; bossesKilled?: number;
  totalRunsPlayed?: number; totalGoldCollected?: number;
  totalChestsOpened?: number; totalDamageDealt?: number;
  totalDamageTaken?: number; totalHealingDone?: number;
  totalCriticalHits?: number; longestSurvivalMinutes?: number;
  highestLevelReached?: number;
}

/** Check if a weapon's unlock condition is met against lifetime stats. */
export function meetsWeaponUnlockCondition(
  weapon: WeaponDef, stats: WeaponUnlockStats | null | undefined,
): boolean {
  const cond = weapon.unlockCondition;
  if (!cond) return false;
  if (!stats) return false;
  const cur = stats[cond.statKey] ?? 0;
  return cur >= cond.value;
}

// Runtime unlock overrides — set by goopSystem on load and after each run
// Starts with the three default-unlocked characters
let _runtimeUnlockedCharIds: Set<string> = new Set(['tactical', 'sponge', 'cowboy']);

/**
 * Called by Home.tsx whenever goopState changes.
 * Syncs character unlock status from the save file into the WEAPONS array so
 * that isCharacterUnlocked() and isWeaponUnlocked() both reflect real saves.
 *
 * BETA 19.35 — Added optional `stats` arg. Shared weapons now gate on
 * `unlockCondition` checked against lifetimeStats (was unconditionally
 * `true`). When stats is null/undefined (e.g. ALL_CHARACTER_IDS dev-unlock
 * path) shared weapons stay unlocked — that path is reserved for bot/dev.
 * Force-unlock case is preserved by setting w.unlocked=true when EITHER
 * the condition is met OR all chars are in `ids` (dev-unlock signal).
 */
export function setUnlockedCharacterIds(ids: Set<string>, stats?: WeaponUnlockStats | null): void {
  const base = ['tactical', 'sponge', 'cowboy'];
  const merged = new Set<string>(base);
  ids.forEach(id => merged.add(id));
  _runtimeUnlockedCharIds = merged;

  // Ensure activeWeaponIds is loaded before we mutate it
  initLoadoutSelection();

  // BETA 19.35 — Dev / bot mode signal: when caller passes EVERY character
  // (the ALL_CHARACTER_IDS path in Home.tsx), unlock all shared weapons too
  // so dev playthroughs aren't gated by quest progress.
  const _allCharsForced = ids.has('killa') && ids.has('wizard') && ids.has('viking') &&
                          ids.has('mr_silly') && ids.has('lebron') && ids.has('chad') &&
                          ids.has('pirate');

  let weaponSetChanged = false;
  for (const w of WEAPONS) {
    if (w.characterId === 'shared') {
      // BETA 19.35 — Shared weapons gate on quest condition vs lifetimeStats.
      // Force-unlock when caller signals "all-chars dev mode".
      w.unlocked = _allCharsForced || (w.unlockCondition == null) || meetsWeaponUnlockCondition(w, stats);
    } else {
      w.unlocked = _runtimeUnlockedCharIds.has(w.characterId);
    }
    // Auto-check weapon when its character unlocks
    if (w.unlocked && !activeWeaponIds.has(w.id)) {
      activeWeaponIds.add(w.id);
      weaponSetChanged = true;
    }
  }
  if (weaponSetChanged) {
    saveSet(WEAPONS_KEY, activeWeaponIds);
  }
}

export const WEAPONS: WeaponDef[] = [
  { id: 'peanut_rifle',        name: '.50 Cal Sniper',      characterId: 'tactical', characterName: 'Military Guy',  iconUrl: resolveAsset('/weapons/wpn_peanut_rifle_solid.png'),        unlocked: true,  unlockReq: '' },
  { id: 'spatula_slash',       name: 'Barbed Wire Bat',     characterId: 'sponge',   characterName: 'Fat Guy',    iconUrl: resolveAsset('/weapons/wpn_barbed_bat_solid.png'),       unlocked: true,  unlockReq: '' },
  { id: 'revolver',            name: 'The Revolver',        characterId: 'cowboy',   characterName: 'Cowboy',    iconUrl: resolveAsset('/weapons/weapon_03_revolver.png'),            unlocked: true,  unlockReq: '' },
  { id: 'rpk_machine_gun',     name: 'RPK Machine Gun',     characterId: 'killa',    characterName: 'Duck',     iconUrl: resolveAsset('/weapons/wpn_rpk_solid.png'),     unlocked: false, unlockReq: 'Kill 500 enemies' },
  { id: 'fireball_wand',       name: 'Firestarter',         characterId: 'wizard',   characterName: 'Robot Girl',    iconUrl: resolveAsset('/weapons/wpn_flamethrower_solid.png'),       unlocked: false, unlockReq: 'Survive 10 waves' },
  { id: 'viking_axe',          name: 'Viking Axe',          characterId: 'viking',   characterName: 'Viking',    iconUrl: resolveAsset('/weapons/wpn_viking_axe_solid.png'),          unlocked: false, unlockReq: 'Deal 10,000 damage' },
  { id: 'voogoo_rager_toss',   name: 'Beer Grenade', characterId: 'mr_silly', characterName: 'Gen-Z Coolkid', iconUrl: resolveAsset('/weapons/wpn_beer_grenade_solid.png'),        unlocked: false, unlockReq: 'Open 20 GOOP crates' },
  { id: 'tactical_basketball', name: 'Dodge This',          characterId: 'lebron',   characterName: 'Smokeshow',    iconUrl: resolveAsset('/weapons/wpn_dodgeball_solid.png'), unlocked: false, unlockReq: 'Win 5 runs' },
  { id: 'chad_aura',           name: 'Personal Space',      characterId: 'chad',     characterName: 'Buff Guy',      iconUrl: resolveAsset('/weapons/weapon_09_chad_aura.png'),           unlocked: false, unlockReq: 'Reach level 20' },
  { id: 'blunderbuss',         name: 'Sawed-Off',           characterId: 'pirate',   characterName: 'Pirate Peanut',    iconUrl: resolveAsset('/weapons/wpn_sawed_off_solid.png'),         unlocked: false, unlockReq: 'Find the treasure map' },
  { id: 'wpn_banana_bombs',    name: 'Banana Bomb',         characterId: 'bigfoot',  characterName: 'Bigfoot',          iconUrl: resolveAsset('/weapons/wpn_banana_bomb_solid.png'),         unlocked: false, unlockReq: 'Unlock Bigfoot' },

  // ── BETA 13.94: 11 new weapons (Phase 1) ──────────────────────────────────────────
  // No character ownership — these are "shared" weapons available to all characters.
  // BETA 19.35 — Was `unlocked: true` from the start. Per Dylan: "there
  // should be a quest for every weapon to unlock for new users". Each
  // shared weapon now has an unlockCondition checked against lifetimeStats
  // (mirrors the perc + character quest pattern). Bot / dev users still
  // get everything unlocked via setUnlockedCharacterIds(ALL_CHARACTER_IDS)
  // path which forces all `unlocked: true`.
  { id: 'wpn_ultra_beam',      name: 'Ultra Beam',          characterId: 'shared',   characterName: 'Shared',           iconUrl: resolveAsset('/weapons/wpn_ultra_beam_solid.png'),                unlocked: false, unlockReq: 'Kill 3 bosses total',
    unlockCondition: { statKey: 'bossesKilled', value: 3, description: 'Kill 3 bosses total' } },
  { id: 'wpn_the_terminator',  name: 'Toilet Seat Boomerang', characterId: 'shared', characterName: 'Shared',           iconUrl: resolveAsset('/weapons/wpn_toilet_boomerang_solid.png'),            unlocked: false, unlockReq: 'Kill 1,000 enemies total',
    unlockCondition: { statKey: 'totalKills', value: 1000, description: 'Kill 1,000 enemies total' } },
  { id: 'wpn_ugly_kittens',    name: 'Cat Cannon',          characterId: 'shared',   characterName: 'Shared',           iconUrl: resolveAsset('/weapons/wpn_cat_cannon_solid.png'),              unlocked: false, unlockReq: 'Survive 5 min in a single run',
    unlockCondition: { statKey: 'longestSurvivalMinutes', value: 5, description: 'Survive 5 min in a single run' } },
  { id: 'wpn_karen_drones',    name: 'Karen Drones',        characterId: 'greg',     characterName: 'Greg',             iconUrl: resolveAsset('/weapons/wpn_karen_drones.png'),              unlocked: false, unlockReq: 'Unlock Greg',
    unlockCondition: { statKey: 'totalChestsOpened', value: 5, description: 'Open 5 chests total' } },
  { id: 'wpn_poop_sling',      name: 'The Thumper',         characterId: 'shared',   characterName: 'Shared',           iconUrl: resolveAsset('/weapons/wpn_grenade_launcher.png'),         unlocked: false, unlockReq: 'Take 500 damage total',
    unlockCondition: { statKey: 'totalDamageTaken', value: 500, description: 'Take 500 damage total' } },
  { id: 'wpn_squirter',        name: 'Squirter',            characterId: 'cheerleader', characterName: 'Cheerleader',   iconUrl: resolveAsset('/weapons/wpn_squirter.png'),                  unlocked: false, unlockReq: 'Unlock Cheerleader',
    unlockCondition: { statKey: 'totalDamageDealt', value: 50000, description: 'Deal 50,000 damage total' } },
  { id: 'wpn_frozen_darts',    name: 'Brain Freeze',        characterId: 'shared',   characterName: 'Shared',           iconUrl: resolveAsset('/weapons/wpn_frozen_gun_solid.png'),              unlocked: false, unlockReq: 'Kill 300 enemies total',
    unlockCondition: { statKey: 'totalKills', value: 300, description: 'Kill 300 enemies total' } },
  { id: 'wpn_dracs_fang',      name: 'Homemade Chainsaw',   characterId: 'shared',   characterName: 'Shared',           iconUrl: resolveAsset('/weapons/wpn_chainsaw_solid.png'),                unlocked: false, unlockReq: 'Heal 200 HP total',
    unlockCondition: { statKey: 'totalHealingDone', value: 200, description: 'Heal 200 HP total' } },
  { id: 'wpn_shitty_cannon',   name: 'Poop Shoot',          characterId: 'shared',   characterName: 'Shared',           iconUrl: resolveAsset('/weapons/wpn_poop_shoot_solid.png'),             unlocked: false, unlockReq: 'Kill 7 bosses total',
    unlockCondition: { statKey: 'bossesKilled', value: 7, description: 'Kill 7 bosses total' } },
  { id: 'wpn_ex_girlfriend',   name: 'The Ex Girlfriend',   characterId: 'suit_guy', characterName: 'Suit Guy',         iconUrl: resolveAsset('/weapons/wpn_ex_girlfriend_solid.png'),             unlocked: false, unlockReq: 'Unlock Suit Guy',
    unlockCondition: { statKey: 'totalCriticalHits', value: 100, description: 'Land 100 critical hits' } },
  { id: 'wpn_hurray',          name: 'Bad Breakfast',       characterId: 'shared',   characterName: 'Shared',           iconUrl: resolveAsset('/weapons/wpn_toaster_gun_solid.png'),                    unlocked: false, unlockReq: 'Complete 3 runs',
    unlockCondition: { statKey: 'totalRunsPlayed', value: 3, description: 'Complete 3 runs' } },

  // ── BETA 22.58: Weapon System v2 Phase 2 — 5 new shared weapons ──────────
  // unlocked:false + quest = they stay OUT of every pool until earned.
  { id: 'wpn_smg',        name: 'The Intern',         characterId: 'shared', characterName: 'Shared', iconUrl: resolveAsset('/weapons/wpn_smg_solid.png'),        unlocked: false, unlockReq: 'Kill 750 enemies total',
    unlockCondition: { statKey: 'totalKills', value: 750, description: 'Kill 750 enemies total' } },
  { id: 'wpn_rpg',        name: 'RPG-7',              characterId: 'shared', characterName: 'Shared', iconUrl: resolveAsset('/weapons/wpn_rpg_solid.png'),        unlocked: false, unlockReq: 'Kill 5 bosses total',
    unlockCondition: { statKey: 'bossesKilled', value: 5, description: 'Kill 5 bosses total' } },
  { id: 'wpn_crossbow',   name: 'Tactical Crossbow',  characterId: 'shared', characterName: 'Shared', iconUrl: resolveAsset('/weapons/wpn_crossbow_solid.png'),   unlocked: false, unlockReq: 'Deal 100,000 damage total',
    unlockCondition: { statKey: 'totalDamageDealt', value: 100000, description: 'Deal 100,000 damage total' } },
  { id: 'wpn_chain_whip', name: 'Chain Whip',         characterId: 'shared', characterName: 'Shared', iconUrl: resolveAsset('/weapons/wpn_chain_whip_solid.png'), unlocked: false, unlockReq: 'Take 1,000 damage total',
    unlockCondition: { statKey: 'totalDamageTaken', value: 1000, description: 'Take 1,000 damage total' } },
  { id: 'wpn_railgun',    name: 'Railgun',            characterId: 'shared', characterName: 'Shared', iconUrl: resolveAsset('/weapons/wpn_railgun_solid.png'),    unlocked: false, unlockReq: 'Kill 2,000 enemies total',
    unlockCondition: { statKey: 'totalKills', value: 2000, description: 'Kill 2,000 enemies total' } },
];

// ── Storage keys ──────────────────────────────────────────────────────────────
const PERCS_KEY = 'goopzone_active_percs';
const BULLSHIIII_KEY = 'goopzone_active_bullshiiii';
const WEAPONS_KEY = 'goopzone_active_weapons';

// BETA 21.61 — Loadout failsafe (Dylan). Minimum buncha-bullshit items that must
// stay checked, mirroring the existing perc/weapon min-4 rule. Prevents shrinking
// the item pool down to only legendaries (which would dodge the luck/rarity system).
export const MIN_BULLSHIIII = 8;
// LOCKED BASE ITEMS — always in the pool, can't be unchecked. A spread of low-rarity
// commons so the drop pool can never become 100% legendary (Megabonk's one good
// guardrail, done by validation instead of their crash-on-empty-rarity bug). The
// player can still add every legendary on top; these just guarantee a common floor.
export const ALWAYS_ON_BULLSHIIII_IDS = new Set<string>(['C01', 'C02', 'C03', 'C05', 'C06', 'C07', 'C08']);
// BETA 22.11 — Dylan: even in dev mode a FLOOR of starter weapons + common percs must stay
// LOCKED (always checked, never uncheckable), so you can't strip the pool to all-legendary
// ("i can just do legendary everything. that is broken"). These join the always-on items above.
//   Weapons: a balanced starter kit — basic rifle, pistol, melee, throwable.
export const LOCKED_WEAPON_IDS = new Set<string>(['peanut_rifle', 'revolver', 'spatula_slash', 'voogoo_rager_toss']);
//   Percs: the 7 basic stat percs (T01-T07: Steroids/Adderall/Vitamins/Not Crack/Reflexes/Vision/Greed).
export const LOCKED_PERC_IDS = new Set<string>(['T01', 'T02', 'T03', 'T04', 'T05', 'T06', 'T07']);

// ── Internal state ────────────────────────────────────────────────────────────
// Sets contain the IDs of items that are CHECKED (active).
let activePercIds: Set<string> = new Set();
let activeBullshiiiiIds: Set<string> = new Set();
let activeWeaponIds: Set<string> = new Set();
let initialized = false;

// ── Initialization ────────────────────────────────────────────────────────────
/** Load saved selections from localStorage, or default all unlocked items to checked. */
export function initLoadoutSelection(): void {
  if (initialized) return;

  // Percs
  const savedPercs = safeLoadSet(PERCS_KEY);
  if (savedPercs) {
    activePercIds = savedPercs;
  } else {
    // Default: all percs with 'default' unlock condition are checked (available from start)
    activePercIds = new Set(
      PEANUT_PERCS.filter(p => p.unlockCondition.type === 'default').map(p => p.id)
    );
  }

  // Bullshiiii
  const savedBull = safeLoadSet(BULLSHIIII_KEY);
  if (savedBull) {
    activeBullshiiiiIds = savedBull;
  } else {
    // Default: all unlocked items are checked
    activeBullshiiiiIds = new Set(
      BUNCHA_BULLSHIIII.filter(b => b.starterPool).map(b => b.id)
    );
  }

  // Weapons
  const savedWeapons = safeLoadSet(WEAPONS_KEY);
  if (savedWeapons) {
    activeWeaponIds = savedWeapons;
  } else {
    // Default: only Sniper Rifle and Revolver are checked
    activeWeaponIds = new Set(
      WEAPONS.filter(w => w.unlocked).map(w => w.id)
    );
  }

  // BETA 22.11 — force the LOCKED floors on, no matter what the saved set says, so the always-on
  // starter weapons + common percs + common items can never be missing (a saved set from before
  // these were locked, or any future edit, can't strip them).
  LOCKED_WEAPON_IDS.forEach(id => activeWeaponIds.add(id));
  LOCKED_PERC_IDS.forEach(id => activePercIds.add(id));
  ALWAYS_ON_BULLSHIIII_IDS.forEach(id => activeBullshiiiiIds.add(id));

  initialized = true;
}

// ── Toggle ────────────────────────────────────────────────────────────────────
/** Toggle a peanut perc on/off. Returns the new checked state. */
export function togglePerc(id: string): boolean {
  initLoadoutSelection();
  if (activePercIds.has(id)) {
    if (LOCKED_PERC_IDS.has(id)) return true;  // BETA 22.11 — locked base perc, can't be unchecked
    if (activePercIds.size <= 4) return true; // minimum 4 percs required
    activePercIds.delete(id);
    saveSet(PERCS_KEY, activePercIds);
    return false;
  } else {
    activePercIds.add(id);
    saveSet(PERCS_KEY, activePercIds);
    return true;
  }
}

/** Toggle a buncha bullshiiii item on/off. Returns the new checked state. */
export function toggleBullshiiii(id: string): boolean {
  initLoadoutSelection();
  if (activeBullshiiiiIds.has(id)) {
    // BETA 21.61 — locked base items can't be unchecked (guarantee a common floor)…
    if (ALWAYS_ON_BULLSHIIII_IDS.has(id)) return true;
    // …and never drop below the minimum (mirrors percs/weapons).
    if (activeBullshiiiiIds.size <= Math.min(MIN_BULLSHIIII, totalSelectableBullshiiii())) return true;
    activeBullshiiiiIds.delete(id);
    saveSet(BULLSHIIII_KEY, activeBullshiiiiIds);
    return false;
  } else {
    activeBullshiiiiIds.add(id);
    saveSet(BULLSHIIII_KEY, activeBullshiiiiIds);
    return true;
  }
}

/** Total buncha-bullshit items that can be selected (active, non-shelved). */
function totalSelectableBullshiiii(): number {
  return BUNCHA_BULLSHIIII.filter(b => b.status === 'active').length;
}

/** Returns true if unchecking another item would violate the minimum-items rule. */
export function isBullshiiiiAtMinimum(): boolean {
  initLoadoutSelection();
  return activeBullshiiiiIds.size <= Math.min(MIN_BULLSHIIII, totalSelectableBullshiiii());
}

/** Returns true if this item is a locked base item (always on, can't be unchecked). */
export function isBullshiiiiLocked(id: string): boolean {
  return ALWAYS_ON_BULLSHIIII_IDS.has(id);
}

/** BETA 22.11 — locked base perc (always checked, can't be unchecked, even in dev). */
export function isPercLocked(id: string): boolean {
  return LOCKED_PERC_IDS.has(id);
}

/** BETA 22.11 — locked starter weapon (always checked, can't be unchecked, even in dev). */
export function isWeaponLocked(id: string): boolean {
  return LOCKED_WEAPON_IDS.has(id);
}

/** Toggle a weapon on/off. Returns the new checked state. */
export function toggleWeapon(id: string): boolean {
  initLoadoutSelection();
  if (activeWeaponIds.has(id)) {
    if (LOCKED_WEAPON_IDS.has(id)) return true;  // BETA 22.11 — locked starter weapon, can't be unchecked
    const totalUnlocked = WEAPONS.filter(w => w.unlocked).length;
    if (activeWeaponIds.size <= Math.min(4, totalUnlocked)) return true; // minimum enforced
    activeWeaponIds.delete(id);
    saveSet(WEAPONS_KEY, activeWeaponIds);
    return false;
  } else {
    activeWeaponIds.add(id);
    saveSet(WEAPONS_KEY, activeWeaponIds);
    return true;
  }
}

// ── Query ─────────────────────────────────────────────────────────────────────
/** Is a specific perc checked? */
export function isPercActive(id: string): boolean {
  initLoadoutSelection();
  return activePercIds.has(id);
}

/** Is a specific bullshiiii item checked? */
export function isBullshiiiiActive(id: string): boolean {
  initLoadoutSelection();
  return activeBullshiiiiIds.has(id);
}

/** Is a specific weapon checked? */
export function isWeaponActive(id: string): boolean {
  initLoadoutSelection();
  return activeWeaponIds.has(id);
}

/** Is a weapon unlocked? */
export function isWeaponUnlocked(weaponId: string): boolean {
  const weapon = WEAPONS.find(w => w.id === weaponId);
  return weapon ? weapon.unlocked : false;
}

/** Is a character unlocked? (based on whether their weapon is unlocked) */
export function isCharacterUnlocked(characterId: string): boolean {
  const weapon = WEAPONS.find(w => w.characterId === characterId);
  return weapon ? weapon.unlocked : false;
}

/** Get all checked peanut percs as full PeanutPerc objects. */
export function getActivePercs(): PeanutPerc[] {
  initLoadoutSelection();
  return PEANUT_PERCS.filter(p => activePercIds.has(p.id));
}

/** Get all checked buncha bullshiiii items as full BullshiiiiItem objects. */
export function getActiveBullshiiiiItems(): BullshiiiiItem[] {
  initLoadoutSelection();
  return BUNCHA_BULLSHIIII.filter(b => activeBullshiiiiIds.has(b.id));
}

/** Get all checked weapon IDs. */
export function getActiveWeapons(): WeaponDef[] {
  initLoadoutSelection();
  return WEAPONS.filter(w => activeWeaponIds.has(w.id));
}

/** Returns true if unchecking this perc would violate the minimum-4 rule. */
export function isPercAtMinimum(): boolean {
  initLoadoutSelection();
  return activePercIds.size <= 4;
}

/** Returns true if unchecking this weapon would violate the minimum rule. */
export function isWeaponAtMinimum(): boolean {
  initLoadoutSelection();
  const totalUnlocked = WEAPONS.filter(w => w.unlocked).length;
  return activeWeaponIds.size <= Math.min(4, totalUnlocked);
}

/** Get count of checked percs. */
export function getActivePercCount(): number {
  initLoadoutSelection();
  return activePercIds.size;
}

/** Get count of checked bullshiiii items. */
export function getActiveBullshiiiiCount(): number {
  initLoadoutSelection();
  return activeBullshiiiiIds.size;
}

/** Get count of checked weapons. */
export function getActiveWeaponCount(): number {
  initLoadoutSelection();
  return activeWeaponIds.size;
}

// ── Reset ─────────────────────────────────────────────────────────────────────
/** Reset all selections to default (all unlocked = checked). */
export function resetLoadoutSelection(): void {
  activePercIds = new Set(
    PEANUT_PERCS.filter(p => p.unlockCondition.type === 'default').map(p => p.id)
  );
  activeBullshiiiiIds = new Set(
    BUNCHA_BULLSHIIII.filter(b => b.starterPool).map(b => b.id)
  );
  activeWeaponIds = new Set(
    WEAPONS.filter(w => w.unlocked).map(w => w.id)
  );
  saveSet(PERCS_KEY, activePercIds);
  saveSet(BULLSHIIII_KEY, activeBullshiiiiIds);
  saveSet(WEAPONS_KEY, activeWeaponIds);
}

// ── Bot Mode ──────────────────────────────────────────────────────────────────
/**
 * Activate every perc, item, and weapon in-memory without saving to localStorage.
 * Characters are handled separately via setUnlockedCharacterIds().
 * Called when bot mode is enabled so the loadout screen shows everything checked.
 */
export function activateAllForBotMode(): void {
  initLoadoutSelection(); // ensure initialized
  // Every perc — regardless of unlockCondition
  activePercIds = new Set(PEANUT_PERCS.map(p => p.id));
  // Every active bullshiiii item
  activeBullshiiiiIds = new Set(BUNCHA_BULLSHIIII.filter(b => b.status === 'active').map(b => b.id));
  // Every weapon — mark them all unlocked so setUnlockedCharacterIds auto-adds them
  for (const w of WEAPONS) w.unlocked = true;
  activeWeaponIds = new Set(WEAPONS.map(w => w.id));
  // Do NOT save to localStorage — bot override shouldn't persist to normal sessions
}

/**
 * BETA 21.61 — Dev HUMAN play. Like activateAllForBotMode (everything ELIGIBLE so a
 * dev can build any loadout), but RESPECTS the dev's saved checkmarks instead of
 * force-checking everything on every menu load. This is what finally lets a dev
 * account UNCHECK an item and have it stick (Dylan's long-standing bug — the old
 * dev path re-checked all in memory each load AND the run ignored the loadout).
 * Bots/Lab keep activateAllForBotMode (full pool, no persistence).
 */
export function activateAllForDevMode(): void {
  initialized = false;     // force a fresh load of the saved checked sets…
  initLoadoutSelection();  // …from localStorage (or the defaults).
  for (const w of WEAPONS) w.unlocked = true; // every weapon eligible to check

  // First-ever dev session (nothing saved yet): default to EVERYTHING checked so the
  // dev starts from a full pool and unchecking is purely subtractive. Persist it.
  if (!safeLoadSet(BULLSHIIII_KEY)) {
    activeBullshiiiiIds = new Set(BUNCHA_BULLSHIIII.filter(b => b.status === 'active').map(b => b.id));
    saveSet(BULLSHIIII_KEY, activeBullshiiiiIds);
  }
  if (!safeLoadSet(PERCS_KEY)) {
    activePercIds = new Set(PEANUT_PERCS.map(p => p.id));
    saveSet(PERCS_KEY, activePercIds);
  }
  if (!safeLoadSet(WEAPONS_KEY)) {
    activeWeaponIds = new Set(WEAPONS.map(w => w.id));
    saveSet(WEAPONS_KEY, activeWeaponIds);
  }
  // Locked base floors are always checked, no matter the saved set (BETA 22.11 — + weapons/percs).
  ALWAYS_ON_BULLSHIIII_IDS.forEach(id => activeBullshiiiiIds.add(id));
  LOCKED_WEAPON_IDS.forEach(id => activeWeaponIds.add(id));
  LOCKED_PERC_IDS.forEach(id => activePercIds.add(id));
}

/**
 * Restore the loadout to whatever the player had saved in localStorage.
 * Call this when bot mode is turned off.
 */
export function restoreLoadoutFromStorage(): void {
  // Force re-init from localStorage on next access
  initialized = false;
  initLoadoutSelection();
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function safeLoadSet(key: string): Set<string> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return new Set(arr);
    return null;
  } catch {
    return null;
  }
}

function saveSet(key: string, set: Set<string>): void {
  try {
    localStorage.setItem(key, JSON.stringify(Array.from(set)));
  } catch {
    // localStorage might be full or unavailable — silently fail
  }
}
